import { DatePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import * as status from 'src/app/models/lookups/invitationStatusLookup';
import { IInvitationListingItem, Invitation } from "src/app/models/invitation";
import { UserFacade } from "src/app/store/user/facade/user.facade";
import { GeneralSelectionFacade } from "src/app/store/general-selection/facade/general-selection.facade";
import { BreakpointObserver } from "@angular/cdk/layout";

@Component({
    selector: "invitations",
    templateUrl: "./invitations.component.html",
    styleUrls: ["./invitations.component.scss"]
})

@UntilDestroy()
export class InvitationsComponent implements OnInit {
    
    invitations: IInvitationListingItem[];
    filteredInvitations: IInvitationListingItem[];

    invitationStatuses = status.InvitationStatusLookup;

    selectedStatus: number = status.PENDING;
    
    statusSelectForm: UntypedFormGroup;
    status: UntypedFormControl;

    selectedDate : Date;

    isMobile : boolean;

    showSpinner = false;

    constructor(
        private router: Router, 
        private userFacade: UserFacade, 
        private datePipe: DatePipe,
        private generalSelectionFacade : GeneralSelectionFacade,
        private breakpointObserver : BreakpointObserver)
    {
        this.createForm();
    }

    ngOnInit(): void {
        this.userFacade.getUserInvitations$
        .pipe(untilDestroyed(this))
        .subscribe((invitations) => {
            this.invitations = this.mapData(invitations);
            this.filterInvitations();
        });

        this.generalSelectionFacade.getSelectedDate()
        .pipe(untilDestroyed(this))
        .subscribe((date) => {
            this.selectedDate = date
        });

        this.breakpointObserver
            .observe(["(max-width: 600px)"])
            .pipe(untilDestroyed(this))
            .subscribe(result => {
                this.isMobile = result.matches;
            });

        this.userFacade.invitationsLoading$
        .pipe(untilDestroyed(this))
        .subscribe((isLoading) => {
            this.showSpinner = isLoading;
        })
    }

    createForm() {
        this.status = new UntypedFormControl(this.selectedStatus);

        this.statusSelectForm = new UntypedFormGroup({
            status: this.status
        });
    }

    public statusChange(){
        this.filterInvitations();
    }

    public filterInvitations(){
        //There is technically no expired status so we fake it by selecting pending invitations that have expired
        if (this.selectedStatus === status.EXPIRED) {
            this.filteredInvitations = this.invitations.filter(i => i.status === status.PENDING && i.isExpired === true)
        }
        //Similarily we only want the pending status to show active pending invitations
        else if (this.selectedStatus === status.PENDING) {
            this.filteredInvitations = this.invitations.filter(i => i.status === status.PENDING && i.isExpired === false)
        }
        else {
            this.filteredInvitations = this.invitations.filter(i => i.status === this.selectedStatus);
        }
    }

    get isPending() {
        return this.selectedStatus == status.PENDING
    }

    get isAccepted() {
        return this.selectedStatus == status.ACCEPTED
    }

    get isRejected() {
        return this.selectedStatus == status.REJECTED
    }

    get isExpired() {
        return this.selectedStatus == status.EXPIRED
    }

    get isCancelled() {
        return this.selectedStatus == status.CANCELLED
    }

    get statusName() {
        return this.invitationStatuses.find(x => x.value === this.selectedStatus).key.toLowerCase();
    }

    public mapData(invitations : Invitation[]){
        return invitations.map((invitation) => {
            return {
                legalEntityName : invitation.legalEntityName,
                expiresAt: this.datePipe.transform(invitation.expiresAt, 'EEE d MMM yyyy').toString(),
                status : invitation.status,
                isExpired : invitation.isExpired,
                invitationId : invitation.invitationId
            } as IInvitationListingItem;
        });
    }

    public acceptInvitation(invitationId : number){
        this.userFacade.acceptUserInvitiation(invitationId);
    }

    public rejectInvitation(invitationId: number){
        this.userFacade.rejectUserInvitiation(invitationId);
    }

    public onResetAndClose() {
        setTimeout(() => {
            this.router.navigate([{ outlets: { 'sidebar': null } }]);
        }, 300);
    }
}